import { withStyles, WithStyles } from '@material-ui/core'
import { useCallback } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { toast } from 'react-toastify'
import {
  saveBeforeLoginApartmentInfo,
  addBeforeLoginApartmentMedia,
  deleteBeforeLoginApartmentMedia,
  reorderBeforeLoginApartmentMedia,
  confirmBeforeLoginApartmentMedia,
  confirmBeforeLoginApartment,
  stepBack,
  stepForward,
  stepClick,
  signUpBeforeLogin
} from '../../../actions/apartments'
import { navigate } from '../../../actions/routes'
import {
  Navigate,
  UpdateApartmentInformation,
  AddApartmentMedia
} from '../../../actions/types'
import Container from '../../../components/Container'
import {
  selectBeforeLoginApartment,
  selectBeforeLoginMedia
} from '../../../selectors/apartments'
import { selectIsMobile } from '../../../selectors/users'
import { apartmentStyles } from '../../../styles'
import i18n from '../../../translations/i18n'
import { MuiClasses } from '../../../types'
import { BeforeLoginApartment } from '../../../types/apartments'
import { ImageFileWithObjectURL } from '../../../types/files'
import { StateInterface } from '../../../types/states'
import { bindSubmitActionToPromise } from '../../../utils/forms'
import { useSessionTimeout } from '../../../utils/useSessionTimeout'
import NewApplicationProgress from '../../NewApplication/Progress'
import { BeforeLoginSteps } from './Steps'

type ParamsProps = {
  step: number
}

interface MatchProps {
  params: ParamsProps
}

interface PropsFromState {
  apartment: BeforeLoginApartment | undefined
  media: ImageFileWithObjectURL[]
  isMobile: any
}

interface DispatchProps {
  doNavigate: Navigate
  doStepBack: () => void
  doStepForward: () => void
  saveApartmentInfo: UpdateApartmentInformation
  addMedia: AddApartmentMedia
  deleteMedia: (arg: any) => any
  reorderMedia: (arg: any) => any
  confirmMedia: (args: any) => void
  confirmApartment: (args: any) => void
  doStepClick: (step: number) => any
  signUpBeforeLogin: (args: any) => void
}

interface OwnProps {
  match: MatchProps
  t: i18n.TFunction
  classes: MuiClasses
}

interface BeforeLoginStepperProps
  extends OwnProps,
    DispatchProps,
    PropsFromState,
    WithTranslation,
    WithStyles {}

export interface BeforeLoginStepProps extends BeforeLoginStepperProps {
  currentStep: number
  beforeLogin: boolean
  isMobile: any
}

const SESSION_TIMEOUT_MS = 60 * 60 * 1000

const BeforeLoginStepper = (stepperProps: BeforeLoginStepperProps) => {
  const {
    match: {
      params: { step }
    },
    classes,
    isMobile,
    doStepBack,
    signUpBeforeLogin,
    doNavigate,
    t
  } = stepperProps
  const currentStep = Number(step)

  const handleTimeout = useCallback(() => {
    doNavigate('/create/apartment/steps/0')
    toast.info(t('timeoutMessage'), { autoClose: false })
  }, [doNavigate, t])

  useSessionTimeout(SESSION_TIMEOUT_MS, handleTimeout)

  const beforeLoginStepProps: BeforeLoginStepProps = {
    ...stepperProps,
    currentStep,
    classes,
    beforeLogin: true,
    isMobile,
    doStepBack,
    signUpBeforeLogin
  }
  return (
    <Container
      styles={{ maxWidth: '1131px', margin: 'auto', padding: '8px 20px' }}
    >
      <NewApplicationProgress
        progress={currentStep === 0 ? 30 : ((currentStep + 1) / 12) * 70 + 30}
      />
      <BeforeLoginSteps {...beforeLoginStepProps} />
    </Container>
  )
}

const mapStateToProps = (state: StateInterface): PropsFromState => ({
  apartment: selectBeforeLoginApartment(state),
  media: selectBeforeLoginMedia(state),
  isMobile: selectIsMobile(state)
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  saveApartmentInfo: bindSubmitActionToPromise(
    dispatch,
    saveBeforeLoginApartmentInfo
  ),
  addMedia: bindSubmitActionToPromise(dispatch, addBeforeLoginApartmentMedia),
  deleteMedia: (objectURL: string) =>
    dispatch(deleteBeforeLoginApartmentMedia(objectURL)),
  reorderMedia: ({
    oldIndex,
    newIndex
  }: {
    oldIndex: number
    newIndex: number
  }) => dispatch(reorderBeforeLoginApartmentMedia({ oldIndex, newIndex })),
  confirmMedia: bindSubmitActionToPromise(
    dispatch,
    confirmBeforeLoginApartmentMedia
  ),
  signUpBeforeLogin: bindSubmitActionToPromise(dispatch, signUpBeforeLogin),
  confirmApartment: bindSubmitActionToPromise(
    dispatch,
    confirmBeforeLoginApartment
  ),
  doStepBack: () => dispatch(stepBack({ beforeLogin: true })),
  doStepForward: () => dispatch(stepForward({ beforeLogin: true })),
  doNavigate: (toPath: string) => dispatch(navigate(toPath)),
  doStepClick: (step: number) =>
    dispatch(stepClick({ step, beforeLogin: true }))
})

export default connect<PropsFromState, DispatchProps, OwnProps, StateInterface>(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(apartmentStyles)(withTranslation()(BeforeLoginStepper)))
